/// <amd-module name="Build/Common/Library/Legacy/Stores/Queue" />
import { Action } from "Build/Flux/Action";
import { Store } from "Build/Flux/Store";

import { AgentPoolQueue } from "Build/Client/Contracts/Build";

import { TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";
import { QueueSource } from "DistributedTask/Common/Library/Sources/Queue";

import { IVssPageContext } from "VSS/Platform/Context";

import { IQueueBuildActionCreatorOptions, IQueueBuildActionHub } from "./Queue.types";

export interface IQueueStoreOptions {
    pageContext: IVssPageContext;
    project: string;
    actionHub?: QueueActionHub;
}

export class QueueStore extends Store {
    public static key = "build-commands-queue-store";

    private _actionCreator: QueueActionCreator;
    private _actionHub: QueueActionHub;
    private _queues: TaskAgentQueue[] = [];
    private _selectedQueue: AgentPoolQueue;

    private _queuesInitializing = false;

    constructor(options: IQueueStoreOptions) {
        super();
        this._actionHub = options.actionHub ? options.actionHub : new QueueActionHub();
        this._actionCreator = new QueueActionCreator({
            actionHub: this._actionHub,
            source: new QueueSource({
                pageContext: options.pageContext,
                project: options.project
            })
        });

        this._actionHub.queuesUpdated.addListener(this._queuesUpdated);
        this._actionHub.queueSelected.addListener(this._queueSelected);
    }

    public getQueues() {
        if (!this._queuesInitializing) {
            this._queuesInitializing = true;
            this._actionCreator.fetchQueues();
        }

        // filter out any queues that have a pool that is flagged as "legacy"
        // note that "legacy" can equate to visibility for migration purposes and is not necessarily
        // an indication that the pool is being deprecated
        return this._queues.filter(queue => !queue.pool || (queue.pool && !queue.pool.isLegacy));
    }

    public getSelectedQueue(): AgentPoolQueue {
        return this._selectedQueue;
    }

    public getActionHub(): QueueActionHub {
        return this._actionHub;
    }

    public dispose() {
        this._actionHub.queuesUpdated.removeListener(this._queuesUpdated);
        this._actionHub.queueSelected.removeListener(this._queueSelected);
    }

    private _queuesUpdated = (queues: TaskAgentQueue[]) => {
        this._queues = queues;
        this.emitChanged();
    }

    private _queueSelected = (selectedAgentQueue: TaskAgentQueue) => {
        let queue: AgentPoolQueue;
        if (selectedAgentQueue) {
            queue = {
                id: selectedAgentQueue.id,
                name: selectedAgentQueue.name
            } as AgentPoolQueue;

            this._selectedQueue = queue;
            this.emitChanged();
        }
    }
}

export class QueueActionCreator {
    private _actionHub: IQueueBuildActionHub;
    private _source: QueueSource;

    constructor(options: IQueueBuildActionCreatorOptions) {
        this._source = options.source;
        this._actionHub = options.actionHub;
    }

    public fetchQueues() {
        this._source.getQueues().then((queues) => {
            this._actionHub.queuesUpdated.invoke(queues);
        });
    }

    public selectQueue(queue: TaskAgentQueue) {
       this._actionHub.queueSelected.invoke(queue);
    }
}

export class QueueActionHub implements IQueueBuildActionHub {
    private _queuesUpdated: Action<TaskAgentQueue[]>;
    private _queueSelected: Action<TaskAgentQueue>;

    constructor() {
        this._queuesUpdated = new Action<TaskAgentQueue[]>();
        this._queueSelected = new Action<TaskAgentQueue>();
    }

    public get queuesUpdated(): Action<TaskAgentQueue[]> {
        return this._queuesUpdated;
    }

    public get queueSelected(): Action<TaskAgentQueue> {
        return this._queueSelected;
    }
}
